import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogActions, Box, Button } from '@mui/material'
import { Files, HardDrive, CheckCircle } from 'lucide-react'
import WizardDialog from './shared/WizardDialog'
import {
  WizardStepRestoreFiles,
  WizardStepRestoreDestination,
  WizardStepRestoreReview,
} from './wizard'
import FileExplorerDialog from './FileExplorerDialog'
import { sshKeysAPI } from '../services/api'
import type { Archive, Repository } from '../types'
import type { RestoreLayout, RestorePathMetadata } from '../utils/restorePaths'
import { DEFAULT_RESTORE_LAYOUT } from '../utils/restorePaths'

interface SSHConnection {
  id: number
  host: string
  username: string
  port: number
  ssh_key_id: number
  default_path?: string
  mount_point?: string
  status: string
}

interface ArchiveFile {
  path: string
  type: 'file' | 'directory'
  mode: string
  user: string
  group: string
  size: number
  mtime: string
  healthy: boolean
}

interface RestoreWizardProps {
  open: boolean
  onClose: () => void
  archive: Pick<Archive, 'id' | 'name'>
  repository: Repository
  repositoryType: string
  onRestore: (data: RestoreData) => void
  /** Paths chosen elsewhere (the archive Files tab). When given, the wizard
   *  opens on the destination step with these already selected. */
  initialSelectedPaths?: string[]
  initialSelectedItems?: RestorePathMetadata[]
}

export interface RestoreData {
  selected_paths: string[]
  destination_type: 'local' | 'ssh'
  destination_connection_id: number | null
  restore_strategy: 'original' | 'custom'
  custom_path: string | null
  restore_layout: RestoreLayout
  path_metadata: RestorePathMetadata[]
}

interface WizardState {
  // Step 0: Files
  selectedPaths: string[]
  selectedItems: RestorePathMetadata[]

  // Step 1: Destination
  destinationType: 'local' | 'ssh'
  destinationConnectionId: number | ''

  // Step 2: Path
  restoreStrategy: 'original' | 'custom'
  customPath: string
  restoreLayout: RestoreLayout
}

const initialState: WizardState = {
  selectedPaths: [],
  selectedItems: [],
  destinationType: 'local',
  destinationConnectionId: '',
  restoreStrategy: 'original',
  customPath: '',
  restoreLayout: DEFAULT_RESTORE_LAYOUT,
}

const RestoreWizard = ({
  open,
  onClose,
  archive,
  repository,
  repositoryType,
  onRestore,
  initialSelectedPaths,
  initialSelectedItems,
}: RestoreWizardProps) => {
  const { t } = useTranslation()
  const [activeStep, setActiveStep] = useState(0)
  const [wizardState, setWizardState] = useState<WizardState>(initialState)
  const [sshConnections, setSshConnections] = useState<SSHConnection[]>([])
  const wasOpenRef = useRef(false)

  // File explorer state
  const [showPathExplorer, setShowPathExplorer] = useState(false)

  // Step definitions
  const steps = useMemo(
    () => [
      { key: 'files', label: t('restoreWizard.steps.files'), icon: <Files size={14} /> },
      {
        key: 'destination',
        label: t('restoreWizard.steps.destination'),
        icon: <HardDrive size={14} />,
      },
      { key: 'review', label: t('restoreWizard.steps.review'), icon: <CheckCircle size={14} /> },
    ],
    [t]
  )

  // Load SSH connections
  const loadSshConnections = async () => {
    try {
      const connectionsRes = await sshKeysAPI.getSSHConnections()
      const connections = connectionsRes.data?.connections || []
      setSshConnections(Array.isArray(connections) ? connections : [])
    } catch (error) {
      console.error('Failed to load SSH connections:', error)
      setSshConnections([])
    }
  }

  // Initialize and reset on dialog open
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Dialog just opened (was closed before)
      const preselected = initialSelectedPaths ?? []
      // A caller can hand over paths with no metadata, or with metadata for
      // only some of them; every preselected path still needs an entry, or
      // the restore goes out with no path_metadata for it.
      const suppliedItems = new Map((initialSelectedItems ?? []).map((item) => [item.path, item]))
      setActiveStep(preselected.length > 0 ? 1 : 0)
      setWizardState({
        ...initialState,
        selectedPaths: preselected,
        selectedItems: preselected.map(
          (path) => suppliedItems.get(path) ?? { path, type: 'file' as const }
        ),
      })
      loadSshConnections()
      wasOpenRef.current = true
    } else if (!open && wasOpenRef.current) {
      // Dialog just closed
      wasOpenRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Force destinationType to 'local' for SSH repositories (SSH-to-SSH not supported)
  useEffect(() => {
    if (open && repositoryType === 'ssh' && wizardState.destinationType === 'ssh') {
      setWizardState((prev) => ({
        ...prev,
        destinationType: 'local',
        destinationConnectionId: '',
      }))
    }
  }, [open, repositoryType, wizardState.destinationType])

  // Handle state changes
  const handleStateChange = (updates: Partial<WizardState>) => {
    // Prevent SSH destination for SSH repositories
    if (repositoryType === 'ssh' && updates.destinationType === 'ssh') {
      return // Silently ignore
    }
    setWizardState((prev) => {
      const next: WizardState = { ...prev, ...updates }

      if (updates.selectedPaths && !updates.selectedItems) {
        const selectedPathSet = new Set(updates.selectedPaths)
        const retainedItems = prev.selectedItems.filter((item) => selectedPathSet.has(item.path))
        const retainedPathSet = new Set(retainedItems.map((item) => item.path))
        const fallbackItems = updates.selectedPaths
          .filter((path) => !retainedPathSet.has(path))
          .map((path) => ({ path, type: 'file' as const }))

        next.selectedItems = [...retainedItems, ...fallbackItems]
      }

      if (updates.restoreStrategy === 'original') {
        next.restoreLayout = DEFAULT_RESTORE_LAYOUT
      }

      return next
    })
  }

  // Handle SSH connection selection
  const handleSshConnectionSelect = (connectionId: number) => {
    const connection = sshConnections.find((c) => c.id === connectionId)
    if (connection) {
      handleStateChange({
        destinationConnectionId: connectionId,
        customPath: connection.default_path || wizardState.customPath,
      })
    }
  }

  // Validation
  const canProceed = () => {
    const currentStepKey = steps[activeStep]?.key

    switch (currentStepKey) {
      case 'files':
        // Must have at least one file selected
        return wizardState.selectedPaths.length > 0

      case 'destination':
        // Validate SSH connection selection
        if (wizardState.destinationType === 'ssh' && !wizardState.destinationConnectionId) {
          return false
        }
        // Validate custom path when custom strategy is selected (applies to both local and SSH)
        if (wizardState.restoreStrategy === 'custom' && !wizardState.customPath.trim()) {
          return false
        }
        return true

      case 'review':
        return true

      default:
        return true
    }
  }

  const handleNext = () => {
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleSubmit = () => {
    const data: RestoreData = {
      selected_paths: wizardState.selectedPaths,
      destination_type: wizardState.destinationType,
      destination_connection_id:
        wizardState.destinationType === 'ssh' && wizardState.destinationConnectionId
          ? (wizardState.destinationConnectionId as number)
          : null,
      restore_strategy: wizardState.restoreStrategy,
      custom_path: wizardState.restoreStrategy === 'custom' ? wizardState.customPath : null,
      restore_layout:
        wizardState.restoreStrategy === 'custom'
          ? wizardState.restoreLayout
          : DEFAULT_RESTORE_LAYOUT,
      path_metadata: wizardState.selectedItems.filter((item) =>
        wizardState.selectedPaths.includes(item.path)
      ),
    }

    onRestore(data)
  }

  // Render current step content
  const renderStepContent = () => {
    const currentStepKey = steps[activeStep]?.key

    // Convert selectedPaths to ArchiveFile format for compatibility
    const selectedFiles: ArchiveFile[] = wizardState.selectedPaths.map((path) => ({
      path,
      type: wizardState.selectedItems.find((item) => item.path === path)?.type || 'file',
      mode: '',
      user: '',
      group: '',
      size: 0,
      mtime: '',
      healthy: true,
    }))

    switch (currentStepKey) {
      case 'files':
        return (
          <WizardStepRestoreFiles
            repository={repository}
            archive={archive}
            data={{
              selectedPaths: wizardState.selectedPaths,
              selectedItems: wizardState.selectedItems,
            }}
            onChange={handleStateChange}
          />
        )

      case 'destination':
        return (
          <WizardStepRestoreDestination
            data={{
              destinationType: wizardState.destinationType,
              destinationConnectionId: wizardState.destinationConnectionId,
              restoreStrategy: wizardState.restoreStrategy,
              customPath: wizardState.customPath,
              restoreLayout: wizardState.restoreLayout,
            }}
            selectedItems={wizardState.selectedItems}
            sshConnections={sshConnections}
            repositoryType={repositoryType}
            onChange={(updates) => {
              // Handle SSH connection selection
              if (
                updates.destinationConnectionId &&
                updates.destinationConnectionId !== wizardState.destinationConnectionId
              ) {
                handleSshConnectionSelect(updates.destinationConnectionId as number)
              } else {
                handleStateChange(updates)
              }
            }}
            onBrowsePath={() => setShowPathExplorer(true)}
          />
        )

      case 'review':
        return (
          <WizardStepRestoreReview
            data={{
              destinationType: wizardState.destinationType,
              destinationConnectionId: wizardState.destinationConnectionId,
              restoreStrategy: wizardState.restoreStrategy,
              customPath: wizardState.customPath,
              restoreLayout: wizardState.restoreLayout,
            }}
            selectedFiles={selectedFiles}
            sshConnections={sshConnections}
            archiveName={archive.name}
          />
        )

      default:
        return null
    }
  }

  return (
    <>
      <WizardDialog
        open={open}
        onClose={onClose}
        title={t('restoreWizard.title')}
        subtitle={t('restoreWizard.fromArchive', { archiveName: archive.name })}
        steps={steps}
        currentStep={activeStep}
        onStepClick={setActiveStep}
        stepContentSx={{
          height: { xs: 'auto', md: 450 },
          minHeight: { xs: 'auto', md: 450 },
          p: 0,
        }}
        footer={
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose}>{t('common.buttons.cancel')}</Button>
            <Box sx={{ flex: 1 }} />
            <Button disabled={activeStep === 0} onClick={handleBack}>
              {t('common.buttons.back')}
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed()}
                sx={{ boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
              >
                {t('common.buttons.next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canProceed()}
                sx={{ boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
              >
                {t('restoreWizard.buttons.restore')}
              </Button>
            )}
          </DialogActions>
        }
      >
        {activeStep === 0 ? (
          <Box sx={{ height: '100%', px: 3, py: 2 }}>{renderStepContent()}</Box>
        ) : (
          <Box sx={{ px: 3, py: 2 }}>{renderStepContent()}</Box>
        )}
      </WizardDialog>

      {/* File Explorer Dialog for custom path */}
      {showPathExplorer && (
        <FileExplorerDialog
          key={`path-explorer-${wizardState.destinationType}-${wizardState.destinationConnectionId}`}
          open={showPathExplorer}
          onClose={() => setShowPathExplorer(false)}
          onSelect={(paths) => {
            if (paths.length > 0) {
              handleStateChange({ customPath: paths[0] })
            }
            setShowPathExplorer(false)
          }}
          title={t('restoreWizard.fileExplorer.selectRestoreDestination')}
          initialPath={
            wizardState.destinationType === 'ssh' && wizardState.destinationConnectionId
              ? sshConnections.find((c) => c.id === wizardState.destinationConnectionId)
                  ?.default_path || '/'
              : '/'
          }
          multiSelect={false}
          connectionType={wizardState.destinationType === 'local' ? 'local' : 'ssh'}
          sshConfig={
            wizardState.destinationType === 'ssh' && wizardState.destinationConnectionId
              ? (() => {
                  const conn = sshConnections.find(
                    (c) => c.id === wizardState.destinationConnectionId
                  )
                  return conn
                    ? {
                        ssh_key_id: conn.ssh_key_id,
                        host: conn.host,
                        username: conn.username,
                        port: conn.port,
                      }
                    : undefined
                })()
              : undefined
          }
          selectMode="directories"
          showSshMountPoints={false}
        />
      )}
    </>
  )
}

export default RestoreWizard
